import * as React from 'react';
import { useEffect, useState } from 'react';

import { compose } from 'recompose';
import _ from 'underscore';

import Imgix from 'js/components/Imgix';
import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import { useParams } from 'js/lib/useRouter';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { color } from '@coursera/coursera-ui';

import { PRODUCT_CARD_HEIGHT } from 'bundles/browse/constants';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import BrowseProductCard from 'bundles/payments-common/components/BrowseProductCard';
import Collection, { CollectionPlaceholder } from 'bundles/payments-common/components/LegacyCollection';
import Promotions404 from 'bundles/promotions/components/Promotions404';
import type { PropsFromPromotionDetailsQuery } from 'bundles/promotions/components/Queries';
import {
  PromotionDetailsQuery,
  promotionDetailsQueryOptions,
  useGetProductDataForPromotion,
} from 'bundles/promotions/components/Queries';
import type {
  PromotionDetailsQuery as PromotionDetailsQueryData,
  PromotionDetailsQueryVariables,
} from 'bundles/promotions/components/__generated__/PromotionDetailsQuery';
import { EXCLUDED_SHARE_PROMO_CODES } from 'bundles/promotions/constants';
import getSocialCaptions from 'bundles/promotions/utils/getSocialCaptions';
import getProductsData from 'bundles/promotions/utils/productData';
import {
  getTrackingData,
  removePromoFromUrlFlag,
  savePromotion,
} from 'bundles/promotions/utils/productDiscountPromoUtils';
import { getPromotionExpirationDate } from 'bundles/promotions/utils/promotionDateUtils';
import type { PropsFromLandingPageApi } from 'bundles/promotions/utils/promotionsApiDataUtils';
import { withLandingPageApiData } from 'bundles/promotions/utils/promotionsApiDataUtils';
import SimpleCourseraMetatags from 'bundles/seo/components/SimpleCourseraMetatags';
import ShareButtonWithModal from 'bundles/sharing-common/components/modal/ShareButtonWithModal';

import defaultMainPromoBanner from 'images/promotions/google_next_banner_bg_hd.png';
import defaultSecondBannerImage from 'images/promotions/google_next_banner_hd.png';

import _t from 'i18n!nls/promotions';

import 'css!./__styl__/PromotionsLandingPage';

type PropsFromRouter = {
  promoCode: string;
};

type PropsToComponent = PropsFromRouter & PropsFromPromotionDetailsQuery & PropsFromLandingPageApi;

const PromotionsLandingPage = ({
  promotionData,
  clientPromotionData,
  promoCode,
  promotionDetails,
}: PropsToComponent) => {
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const { loadingServerProducts, productsServerData, loadingClientProducts, productsClientData } =
    useGetProductDataForPromotion(promotionData, clientPromotionData);

  const isActivePromotion = () => {
    if (promotionDetails && promotionData) {
      const startDate = new Date(promotionDetails.startsAt || 0);
      const endDate = new Date(promotionDetails.endsAt || 0);
      const todaysDate = new Date();
      // add support for promotion without end time
      return (startDate < todaysDate || user.isSuperuser()) && (todaysDate < endDate || !promotionDetails.endsAt);
    } else {
      return false;
    }
  };

  useEffect(() => {
    setIsPageLoaded(true);
    if (isActivePromotion()) {
      // `isActivePromotion` checks for the existence of `promotionData`
      const promoCodes = promotionData!.promoCodes;
      // promoCode from route is case insensitive
      // save promo code from promotionData to prevent saving multiple versions of the same code
      const promoCodeToSave = promoCodes.find(
        (defaultPromoCode) => defaultPromoCode.toLowerCase() === promoCode.toLowerCase()
      );
      // save promoCode to local storage to keep track of users who have seen the promotion
      if (promoCodeToSave) {
        savePromotion(promoCodeToSave);
      }
      // remove promo from url flag if it exists
      removePromoFromUrlFlag();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderCollections = (collections: $TSFixMe, loadingProducts: $TSFixMe, productsData: $TSFixMe) => {
    if (!promotionData) {
      // to make TS happy
      throw new Error('do not call renderCollections if there is no active promotion');
    }
    // getTrackingData relies on local storage, so we won't call it until after the page loads
    const trackingData = isPageLoaded ? getTrackingData() : {};
    const { getPromoValueProp } = promotionData;

    return (
      <div className="c-promotionCollections m-t-1 m-b-3">
        {collections.map(({ products, getTitle }: $TSFixMe) => {
          const collectionTitle = getTitle();
          if (loadingProducts) {
            return <CollectionPlaceholder key={collectionTitle} collectionItemHeight={PRODUCT_CARD_HEIGHT} />;
          }
          const { courses, specializations } = productsData;
          const productsWithData = getProductsData(products, courses, specializations);
          const productsWithDataWithQueryParams = productsWithData.map((product: $TSFixMe) => {
            return {
              ...product,
              queryParameters: {
                // eslint-disable-next-line camelcase
                utm_medium: 'coursera',
                // eslint-disable-next-line camelcase
                utm_source: 'promo',
              },
            };
          });

          return (
            !_(productsWithData).isEmpty() && (
              <Collection
                tag={getPromoValueProp()}
                tagClassNames="peach-gradient-tag horizontal-box align-items-vertical-center"
                CollectionItem={BrowseProductCard}
                collectionItemHeight={PRODUCT_CARD_HEIGHT}
                collection={productsWithDataWithQueryParams}
                collectionName={collectionTitle}
                key={collectionTitle}
                trackingData={trackingData}
              />
            )
          );
        })}
      </div>
    );
  };

  // `isActivePromotion` checks for existence of `promotionDetails`
  // non-courserian should not have access to test promo's landing page
  if (!isActivePromotion() || (promotionDetails?.isTest && !user.isCourserian())) {
    return <Promotions404 />;
  }
  const endsAt = promotionDetails!.endsAt;

  const trackingData = getTrackingData();

  const {
    logo1Url,
    logo2Url,
    bannerHeading,
    bannerDescription,
    bannerMainImgUrl,
    bannerSecondImgUrl,
    collections,
    bannerDetails,
    // `isActivePromotion` checks for existence of `promotionData`
  } = promotionData!;

  const { getPromoDisclaimers } = bannerDetails;
  const promoDisclaimers = getPromoDisclaimers && getPromoDisclaimers();
  const bannerDescriptionStyles = Object.assign(
    { color: 'var(--cds-color-neutral-primary)' },
    bannerDescription.styles
  );
  const promoPageTitle = _t('Black Friday Promotion - Learn for Free');

  return (
    <TrackedDiv
      trackingName="promotions_landing_page_shown"
      data={trackingData}
      className="rc-PromotionsLandingPage p-t-1"
    >
      {promoCode === 'blackfriday2020' && (
        <SimpleCourseraMetatags disableCrawlerIndexing={true} title={promoPageTitle} />
      )}
      {(logo1Url || logo2Url) && (
        <div
          className="c-promotionsLogos horizontal-box align-items-absolute-center m-t-1 m-b-2"
          style={{ minHeight: '50px' }}
        >
          {logo1Url && <img className="c-promotionsLogo" src={logo1Url} alt={_t('First Logo')} />}
          {logo1Url && logo2Url && <div className="c-verticalDivider m-l-1 m-r-1" />}
          {logo2Url && <img className="c-promotionsLogo" src={logo2Url} alt={_t('Second Logo')} />}
        </div>
      )}
      <div className="horizontal-box">
        <div
          className="c-promotionsBanner w-100 align-items-spacebetween horizontal-box"
          style={{
            backgroundImage: `url("${bannerMainImgUrl || defaultMainPromoBanner}")`,
            backgroundSize: 'cover',
          }}
        >
          <div className="c-promotionsBannerContent p-a-2 vertical-box h-100">
            <h2 style={bannerHeading.styles} className="c-bannerHeading">
              <FormattedHTMLMessage message={bannerHeading.getText()} />
            </h2>
            <div className="promo-banner-button-load-container" style={{ minWidth: '1px', minHeight: '36.5px' }}>
              {!EXCLUDED_SHARE_PROMO_CODES.includes(promoCode) && (
                <ShareButtonWithModal
                  rootClassName="promo-banner-button"
                  utmMediumParam="page_share"
                  utmContentParam="pp"
                  utmCampaignParam="top_button"
                  captions={getSocialCaptions({ expirationDate: getPromotionExpirationDate(endsAt) })}
                />
              )}
            </div>
            <p style={bannerDescriptionStyles} className="c-bannerDescription headline-2-text m-b-2 m-t-1">
              <FormattedHTMLMessage message={bannerDescription.getText()} />
            </p>
            <div className="horizontal-box">
              <div
                className="c-bannerDetails vertical-box align-self-end"
                style={{ color: bannerDetails.color || color.black }}
              >
                {promoDisclaimers &&
                  promoDisclaimers.map((disclaimerText) => (
                    <p className="c-detailsText m-b-0" key={disclaimerText}>
                      <FormattedHTMLMessage message={disclaimerText} endDate={getPromotionExpirationDate(endsAt)} />
                    </p>
                  ))}
              </div>
            </div>
          </div>
        </div>
        {bannerSecondImgUrl && (
          <div className="desktop-only-img-container">
            <Imgix
              src={bannerSecondImgUrl || defaultSecondBannerImage}
              alt=""
              maxHeight="100%"
              imgParams={{ fit: 'crop', auto: 'format,compress', bg: 'FFF' }}
            />
          </div>
        )}
      </div>
      {productsServerData && renderCollections(collections, loadingServerProducts, productsServerData)}
      {isPageLoaded &&
        productsClientData &&
        clientPromotionData &&
        renderCollections(clientPromotionData.collections, loadingClientProducts, productsClientData)}
    </TrackedDiv>
  );
};

const PromotionsLandingPageHOC = compose<PropsToComponent, PropsFromRouter>(
  waitForGraphQL<
    PropsFromRouter,
    PromotionDetailsQueryData,
    PromotionDetailsQueryVariables,
    PropsFromPromotionDetailsQuery
  >(PromotionDetailsQuery, promotionDetailsQueryOptions),
  withLandingPageApiData()
)(PromotionsLandingPage);

export default (props: {}) => {
  const params = useParams();
  return <PromotionsLandingPageHOC {...props} promoCode={params.promoCode} />;
};
